import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { 
  CreditCard, 
  Check, 
  Crown,
  Zap,
  Building2,
  Users,
  FileText,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  Receipt,
  ArrowRight,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const BillingPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [billingInfo, setBillingInfo] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [activeTab, setActiveTab] = useState('plans');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [plansRes, billingRes, invoicesRes, transactionsRes] = await Promise.all([
        api.get('/billing/plans'),
        api.get('/billing/info'),
        api.get('/billing/invoices'),
        api.get('/billing/transactions')
      ]);
      
      setPlans(plansRes.data);
      setBillingInfo(billingRes.data);
      setInvoices(invoicesRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  }, []);

  const checkPaymentStatus = useCallback(async (sessionId) => {
    try {
      const response = await api.get(`/billing/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your plan has been upgraded.');
        fetchData();
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
    }
  }, [fetchData]);

  useEffect(() => {
    fetchData();
    
    // Check for successful payment return
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    
    if (sessionId && success === 'true') {
      checkPaymentStatus(sessionId);
    }
    
    const canceled = searchParams.get('canceled');
    if (canceled === 'true') {
      toast.error('Payment was canceled');
    }
  }, [searchParams, fetchData, checkPaymentStatus]);

  const handleUpgrade = async (planId) => {
    if (planId === 'trial' || planId === 'free') {
      toast.info('This plan is already free');
      return;
    }
    
    try {
      setProcessingPlan(planId);
      
      const response = await api.post('/billing/checkout', {
        plan: planId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
      
      // Handle error detail - it might be a string or array of validation errors
      let errorMessage = 'Failed to start checkout';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Handle validation errors array
          errorMessage = detail.map(err => err.msg || 'Validation error').join(', ');
        }
      }
      
      toast.error(errorMessage);
      setProcessingPlan(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-400/10';
      case 'trial': return 'text-amber-400 bg-amber-400/10';
      case 'past_due': return 'text-red-400 bg-red-400/10';
      case 'canceled': return 'text-slate-400 bg-slate-400/10';
      default: return 'text-slate-400 bg-slate-400/10';
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'basic': return <Zap className="w-6 h-6" />;
      case 'pro': return <Star className="w-6 h-6" />;
      case 'enterprise': return <Crown className="w-6 h-6" />;
      default: return <Clock className="w-6 h-6" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysRemaining = (dateStr) => {
    if (!dateStr) return null;
    const end = new Date(dateStr);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  const currentPlan = billingInfo?.plan || 'trial';
  const trialDaysRemaining = billingInfo?.trial_ends_at ? getDaysRemaining(billingInfo.trial_ends_at) : null;

  return (
    <Layout>
      <div className="space-y-6" data-testid="billing-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Barlow_Condensed'] uppercase tracking-wider">
              Billing & Subscription
            </h1>
            <p className="text-slate-400 mt-1">Manage your subscription and payment methods</p>
          </div>
        </div>

        {/* Current Plan Status */}
        <div className="bg-slate-800 rounded-sm p-6 border border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-amber-500/20 flex items-center justify-center">
                {getPlanIcon(currentPlan)}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {plans.find(p => (p.plan || p.id) === currentPlan)?.name || 'Trial'} Plan
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(billingInfo?.status)}`}>
                    {billingInfo?.status?.toUpperCase() || 'TRIAL'}
                  </span>
                  {billingInfo?.status === 'trial' && trialDaysRemaining !== null && (
                    <span className="text-amber-400 text-sm">
                      {trialDaysRemaining} days remaining
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {billingInfo?.status === 'active' && billingInfo?.current_period_end && (
              <div className="text-right">
                <p className="text-slate-400 text-sm">Next billing date</p>
                <p className="text-white font-medium">{formatDate(billingInfo.current_period_end)}</p>
              </div>
            )}
          </div>
          
          {billingInfo?.payment_method_last4 && (
            <div className="mt-4 pt-4 border-t border-slate-700 flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-slate-400" />
              <span className="text-slate-400">
                {billingInfo.payment_method_brand} ending in {billingInfo.payment_method_last4}
              </span>
            </div>
          )}
        </div>

        {/* Trial Warning */}
        {billingInfo?.status === 'trial' && trialDaysRemaining !== null && trialDaysRemaining <= 7 && (
          <div className="bg-amber-900/30 border border-amber-600/50 rounded-sm p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-amber-400 font-medium">Your trial is ending soon</p>
              <p className="text-amber-400/80 text-sm">Upgrade now to keep access to all features.</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-700">
          <button
            onClick={() => setActiveTab('plans')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'plans' 
                ? 'text-amber-500 border-b-2 border-amber-500' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Plans
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'invoices' 
                ? 'text-amber-500 border-b-2 border-amber-500' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Invoices
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'history' 
                ? 'text-amber-500 border-b-2 border-amber-500' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Payment History
          </button>
        </div>

        {/* Plans Tab */}
        {activeTab === 'plans' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((plan) => {
              // Backend uses 'plan' field instead of 'id'
              const planId = plan.plan || plan.id;
              const isCurrent = currentPlan === planId;
              const isDowngrade = plans.findIndex(p => (p.plan || p.id) === currentPlan) > plans.findIndex(p => (p.plan || p.id) === planId);
              
              return (
                <div 
                  key={planId}
                  className={`bg-slate-800 rounded-sm border p-6 relative ${
                    isCurrent ? 'border-amber-500' : 'border-slate-700'
                  }`}
                >
                  {isCurrent && (
                    <div className="absolute -top-3 left-4 px-2 py-0.5 bg-amber-500 text-black text-xs font-bold rounded">
                      CURRENT
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                      planId === 'enterprise' ? 'bg-purple-500/20 text-purple-400' :
                      planId === 'pro' ? 'bg-amber-500/20 text-amber-400' :
                      planId === 'basic' ? 'bg-cyan-500/20 text-cyan-400' :
                      'bg-slate-700 text-slate-400'
                    }`}>
                      {getPlanIcon(planId)}
                    </div>
                    <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                  </div>
                  
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white">${plan.price}</span>
                    <span className="text-slate-400">/month</span>
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2 text-sm text-slate-300">
                      <Users className="w-4 h-4 text-slate-400" />
                      {plan.max_users === -1 ? 'Unlimited' : plan.max_users} users
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-300">
                      <FileText className="w-4 h-4 text-slate-400" />
                      {plan.max_work_orders === -1 ? 'Unlimited' : plan.max_work_orders} work orders/mo
                    </li>
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                        <Check className="w-4 h-4 text-green-400" />
                        {feature.replace(/_/g, ' ')}
                      </li>
                    ))}
                  </ul>
                  
                  {planId !== 'trial' && !isCurrent && (
                    <Button
                      onClick={() => handleUpgrade(planId)}
                      disabled={processingPlan === planId || isDowngrade}
                      className={`w-full ${
                        isDowngrade 
                          ? 'bg-slate-600 cursor-not-allowed' 
                          : 'bg-amber-500 hover:bg-amber-600 text-black'
                      }`}
                      data-testid={`upgrade-${planId}-btn`}
                    >
                      {processingPlan === planId ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <ArrowRight className="w-4 h-4 mr-2" />
                      )}
                      {isDowngrade ? 'Contact Support' : 'Upgrade'}
                    </Button>
                  )}
                  
                  {isCurrent && planId !== 'trial' && (
                    <div className="text-center text-green-400 text-sm font-medium">
                      <CheckCircle2 className="w-4 h-4 inline mr-1" />
                      Active
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="bg-slate-800 rounded-sm border border-slate-700">
            {invoices.length === 0 ? (
              <div className="p-8 text-center">
                <Receipt className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No invoices yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-slate-400 font-medium">Date</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Plan</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Period</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Amount</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-slate-700/50">
                      <td className="p-4 text-white">{formatDate(invoice.created_at)}</td>
                      <td className="p-4 text-white capitalize">{invoice.plan}</td>
                      <td className="p-4 text-slate-400 text-sm">
                        {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                      </td>
                      <td className="p-4 text-white">${invoice.amount.toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          invoice.status === 'paid' ? 'bg-green-400/10 text-green-400' :
                          invoice.status === 'unpaid' ? 'bg-amber-400/10 text-amber-400' :
                          'bg-slate-400/10 text-slate-400'
                        }`}>
                          {invoice.status.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Payment History Tab */}
        {activeTab === 'history' && (
          <div className="bg-slate-800 rounded-sm border border-slate-700">
            {transactions.length === 0 ? (
              <div className="p-8 text-center">
                <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No payment history</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-slate-400 font-medium">Date</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Description</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Amount</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="border-b border-slate-700/50">
                      <td className="p-4 text-white">{formatDate(tx.created_at)}</td>
                      <td className="p-4 text-white">{tx.description}</td>
                      <td className="p-4 text-white">${tx.amount.toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          tx.status === 'completed' ? 'bg-green-400/10 text-green-400' :
                          tx.status === 'pending' ? 'bg-amber-400/10 text-amber-400' :
                          tx.status === 'failed' ? 'bg-red-400/10 text-red-400' :
                          'bg-slate-400/10 text-slate-400'
                        }`}>
                          {tx.status.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BillingPage;
